import React, { useState } from 'react';
import { Anchor, Button } from '@keplerco/core';
import styles from '../freemium.onboarding.module.css';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { useAppActions, useAppState } from '../../../../overmind';
import { roleInterests, skillInterests, teamDepartments } from '../freemium-data';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';

// Props for the interests page
interface InterestsPageProps {
  onNext: () => void;
  onBack: () => void;
}

/**
 * Interests onboarding step
 * Allows users to select role interests, skill interests, and team/departments
 */
export function InterestsPage({ onNext, onBack }: InterestsPageProps): JSX.Element {
  const actions = useAppActions();
  const { freemiumAccount } = useAppState();

  // State for selections
  const [selectedRoleInterests, setSelectedRoleInterests] = useState<string[]>(
    freemiumAccount?.roleInterests || []
  );
  const [selectedSkillInterests, setSelectedSkillInterests] = useState<string[]>(
    freemiumAccount?.skillInterests || []
  );
  const [selectedTeamDepartments, setSelectedTeamDepartments] = useState<string[]>(
    freemiumAccount?.departmentInterests || []
  );

  // State for search input
  const [roleSearchText, setRoleSearchText] = useState('');
  const [skillSearchText, setSkillSearchText] = useState('');
  const [teamSearchText, setTeamSearchText] = useState('');

  // State for custom items
  const [customRoleInterest, setCustomRoleInterest] = useState('');
  const [customSkillInterest, setCustomSkillInterest] = useState('');
  const [customTeamDepartment, setCustomTeamDepartment] = useState('');

  // Filter interests based on search text
  const filteredRoleInterests = roleInterests.filter(role =>
    role.name.toLowerCase().includes(roleSearchText.toLowerCase())
  );

  const filteredSkillInterests = skillInterests.filter(skill =>
    skill.name.toLowerCase().includes(skillSearchText.toLowerCase())
  );

  const filteredTeamDepartments = teamDepartments.filter(team =>
    team.name.toLowerCase().includes(teamSearchText.toLowerCase())
  );

  // Toggle selection of an interest
  const toggleRoleInterest = (interest: string) => {
    if (selectedRoleInterests.some(i => i === interest)) {
      setSelectedRoleInterests(selectedRoleInterests.filter(i => i !== interest));
    } else {
      setSelectedRoleInterests([...selectedRoleInterests, interest]);
    }
  };

  const toggleSkillInterest = (interest: string) => {
    if (selectedSkillInterests.some(i => i === interest)) {
      setSelectedSkillInterests(selectedSkillInterests.filter(i => i !== interest));
    } else {
      setSelectedSkillInterests([...selectedSkillInterests, interest]);
    }
  };

  const toggleTeamDepartment = (interest: string) => {
    if (selectedTeamDepartments.some(i => i === interest)) {
      setSelectedTeamDepartments(selectedTeamDepartments.filter(i => i !== interest));
    } else {
      setSelectedTeamDepartments([...selectedTeamDepartments, interest]);
    }
  };

  // Add custom interest
  const addCustomRoleInterest = () => {
    if (!customRoleInterest) return;

    setSelectedRoleInterests([...selectedRoleInterests, customRoleInterest]);
    setCustomRoleInterest('');
  };

  const addCustomSkillInterest = () => {
    if (!customSkillInterest) return;

    setSelectedSkillInterests([...selectedSkillInterests, customSkillInterest]);
    setCustomSkillInterest('');
  };

  const addCustomTeamDepartment = () => {
    if (!customTeamDepartment) return;

    setSelectedTeamDepartments([...selectedTeamDepartments, customTeamDepartment]);
    setCustomTeamDepartment('');
  };

  // Handle form submission
  const handleContinue = async () => {
    // Save data to state
    actions.startLoader({ path: PagePath.onboarding, type: FetchType.Sending });
    await actions.updateInterests({
      roleInterests: selectedRoleInterests,
      skillInterests: selectedSkillInterests,
      departmentInterests: selectedTeamDepartments,
    });
    await actions.getFreemiumAccount();
    actions.stopLoader(PagePath.onboarding);

    // Go to next step
    onNext();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      {/* Role Interests Section */}
      <EntitiesCardWidget title="Role Interests" description="Choose the roles that are most relevant to your organization.">
        <div className={styles.interestsSection} style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <h3>Role Interests</h3>
          <p>Select the types of roles that are important to your organization.</p>

          <div className={styles.searchInputContainer}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search for roles or add custom..."
              value={roleSearchText}
              onChange={(e) => setRoleSearchText(e.target.value)}
            />
          </div>

          <div className={styles.interestsList}>
            {filteredRoleInterests.map(role => (
              <div
                key={role.name}
                className={`${styles.interestItem} ${selectedRoleInterests.some(i => i === role.name) ? styles.selected : ''}`}
                onClick={() => toggleRoleInterest(role.name)}
              >
                {role.name}
                <span className={styles.interestCategory}>{role.category}</span>
              </div>
            ))}
          </div>

          <div className={styles.customInterestContainer}>
            <input
              type="text"
              className={styles.customInterestInput}
              placeholder="Add a custom role..."
              value={customRoleInterest}
              onChange={(e) => setCustomRoleInterest(e.target.value)}
            />
            <button
              type="button"
              className={styles.customInterestButton}
              onClick={addCustomRoleInterest}
            >
              Add
            </button>
          </div>

          {selectedRoleInterests.length > 0 && (
            <div className={styles.selectedInterests}>
              <h4>Selected Roles ({selectedRoleInterests.length})</h4>
              <div className={styles.selectedInterestsList}>
                {selectedRoleInterests.map(role => (
                  <div key={role} className={styles.selectedInterestItem}>
                    {role}
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => toggleRoleInterest(role)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </EntitiesCardWidget>

      {/* Skill Interests Section */}
      <EntitiesCardWidget title="Skill Interests" description="Choose the skills that are most relevant to your organization.">
        <div className={styles.interestsSection} style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <h3>Skill Interests</h3>
          <p>Select the types of skills that are important to your organization.</p>

          <div className={styles.searchInputContainer}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search for skills or add custom..."
              value={skillSearchText}
              onChange={(e) => setSkillSearchText(e.target.value)}
            />
          </div>

          <div className={styles.interestsList}>
            {filteredSkillInterests.map(skill => (
              <div
                key={skill.name}
                className={`${styles.interestItem} ${selectedSkillInterests.some(i => i === skill.name) ? styles.selected : ''}`}
                onClick={() => toggleSkillInterest(skill.name)}
              >
                {skill.name}
                <span className={styles.interestCategory}>{skill.category}</span>
              </div>
            ))}
          </div>

          <div className={styles.customInterestContainer}>
            <input
              type="text"
              className={styles.customInterestInput}
              placeholder="Add a custom skill..."
              value={customSkillInterest}
              onChange={(e) => setCustomSkillInterest(e.target.value)}
            />
            <button
              type="button"
              className={styles.customInterestButton}
              onClick={addCustomSkillInterest}
            >
              Add
            </button>
          </div>

          {selectedSkillInterests.length > 0 && (
            <div className={styles.selectedInterests}>
              <h4>Selected Skills ({selectedSkillInterests.length})</h4>
              <div className={styles.selectedInterestsList}>
                {selectedSkillInterests.map(skill => (
                  <div key={skill} className={styles.selectedInterestItem}>
                    {skill}
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => toggleSkillInterest(skill)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </EntitiesCardWidget>

      {/* Team/Departments Section */}
      <EntitiesCardWidget title="Teams & Departments" description="Select the types of teams or departments that exist in your organization.">
        <div className={styles.interestsSection} style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
          <h3>Teams & Departments</h3>
          <p>Select the types of teams or departments that exist in your organization.</p>

          <div className={styles.searchInputContainer}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search for teams or add custom..."
              value={teamSearchText}
              onChange={(e) => setTeamSearchText(e.target.value)}
            />
          </div>

          <div className={styles.interestsList}>
            {filteredTeamDepartments.map(team => (
              <div
                key={team.name}
                className={`${styles.interestItem} ${selectedTeamDepartments.some(i => i === team.name) ? styles.selected : ''}`}
                onClick={() => toggleTeamDepartment(team.name)}>
                {team.name}
                <span className={styles.interestCategory}>{team.category}</span>
              </div>
            ))}
          </div>

          <div className={styles.customInterestContainer}>
            <input
              type="text"
              className={styles.customInterestInput}
              placeholder="Add a custom team or department..."
              value={customTeamDepartment}
              onChange={(e) => setCustomTeamDepartment(e.target.value)}
            />
            <button
              type="button"
              className={styles.customInterestButton}
              onClick={addCustomTeamDepartment}
            >
              Add
            </button>
          </div>

          {selectedTeamDepartments.length > 0 && (
            <div className={styles.selectedInterests}>
              <h4>Selected Teams & Departments ({selectedTeamDepartments.length})</h4>
              <div className={styles.selectedInterestsList}>
                {selectedTeamDepartments.map(team => (
                  <div key={team} className={styles.selectedInterestItem}>
                    {team}
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => toggleTeamDepartment(team)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)}>
        <Anchor className={styles.anchor} onClick={handleBack}>
          Back
        </Anchor>
        <Button type="button" filled onClick={handleContinue}>
          Next
        </Button>
      </footer>
    </div>
  );
}
